import { getCurrentInstance } from 'vue';
import type {
  LocationQuery,
  LocationQueryValue,
  RouteLocation,
  RouteLocationRaw,
} from '#vue-router';

import type {
  RoutesNamesList,
  TypedRouteLocationRawFromName,
} from '@typed-router';

type RoutePath = TypedRouteLocationRawFromName<RoutesNamesList, string>;

export const useRoutes = () => {
  const router = useRouter();

  // Só chama useRoute() quando não estamos em contexto de middleware
  const route = computed(() => {
    // Verifica se estamos em um middleware verificando se getCurrentInstance existe
    if (process.server && !getCurrentInstance()) {
      return null;
    }
    try {
      return useRoute();
    } catch {
      return null;
    }
  });

  const currentRouteName = computed<RoutesNamesList | null>(() => {
    const r = route.value;

    if (!r) return null;

    return getRouteBaseName(r);
  });

  const currentRouteQuery = computed<LocationQuery>(() => {
    return route.value?.query || {};
  });

  function getRouteBaseName(route: RouteLocation) {
    // Extract base name from route (remove locale prefix if any)
    const name = route.name as string;

    if (!name) return null as unknown as RoutesNamesList;

    // Remove locale prefix like 'pt-br___' if present
    const baseName = name.replace(/^[a-z]{2}(-[a-z]{2})?___/, '');

    return baseName as RoutesNamesList;
  }

  function localeRoute(route: RouteLocationRaw) {
    if (typeof route === 'string') {
      return route;
    }

    const resolved = router.resolve(route);

    return resolved.fullPath;
  }

  async function goToLocaleRoute(to: RoutePath) {
    return await navigateTo(localeRoute(to));
  }

  const getSecureQueryParam = (
    query: LocationQueryValue | LocationQueryValue[] | string,
  ) => {
    if (!query) {
      return;
    }

    // Protege contra injeção de script
    return decodeURIComponent(query as string)
      .replace(/&lt;/g, '')
      .replace(/&gt;/g, '')
      .replace(/&quot;/g, '')
      .replace(/&#39;/g, '')
      .replace(/&#96;/g, '')
      .replace('document', '')
      .replace('createElement', '')
      .replace('appendChild', '')
      .replace(/<img[^>]*src=["']?([^"'>]+)["']?[^>]*>/gi, '');
  };

  return {
    route,
    currentRouteName,
    currentRouteQuery,
    getRouteBaseName,
    goToLocaleRoute,
    localeRoute,
    getSecureQueryParam,
  };
};
